import { z } from 'zod';
import { LENDER_NAME_INVALID, EMAIL_INVALID, LOGIN_INVALID } from '../copy';

export const USER_ROLES = ['lender-admin', 'lender-standard', 'lender-read-only'] as const;

/** Person names: at least one alphanumeric, but apostrophes, hyphens and spaces belong here. */
const alphanumeric = z.string().regex(/[a-zA-Z0-9]/, LENDER_NAME_INVALID);

/**
 * An LOS login is anchored, not merely "contains a letter or digit": it becomes half of
 * `urn:los:meridianlink[-mortgage]:lender:<code>:user:<login>`, and OM-4359 AC3 asks for any
 * non-alphanumeric character — a space, a '+' — to surface an error rather than ride through.
 */
const login = z.string().regex(/^[a-zA-Z0-9]+$/, LOGIN_INVALID);

/**
 * The MeridianLink Consumer user name is required only while that integration is selected in
 * Step 2, so the rule cannot live in a single static schema — whether the field is even on screen
 * depends on state the form does not own. Hence a builder rather than a constant.
 */
export function buildUserSchema({
  requireMlConsumerUserName,
  requireMlMortgageUserName = false,
}: {
  requireMlConsumerUserName: boolean;
  requireMlMortgageUserName?: boolean;
}) {
  return z.object({
    firstName: alphanumeric,
    lastName: alphanumeric,
    // Kept in the shape either way so the value is preserved when the field is hidden; it is only
    // the validation that comes and goes.
    mlConsumerUserName: requireMlConsumerUserName ? login : z.string(),
    // Same pattern for the Mortgage login — the per-user name Mortgage requests arrive under.
    mlMortgageUserName: requireMlMortgageUserName ? login : z.string(),
    email: z.string().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, EMAIL_INVALID),
    role: z.enum(USER_ROLES),
  });
}

/** Shape only — for validity checks that do not depend on the LOS selection. */
export const userSchema = buildUserSchema({ requireMlConsumerUserName: false });

export type UserFormValues = z.infer<typeof userSchema>;
